// src/components/ConfirmSignUp.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CognitoUser } from "amazon-cognito-identity-js";
import { userPool } from "../awsConfig";
import { useTheme } from "../theming";
import { Box, Typography, TextField, Button, CircularProgress } from "@mui/material";

const ConfirmSignUp = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    username: "",
    confirmationCode: "",
  });
  const [message, setMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Auto-populate username from localStorage
  useEffect(() => {
    const pendingUsername = localStorage.getItem("pendingUsername");
    if (pendingUsername) {
      setFormData((prev) => ({ ...prev, username: pendingUsername }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);
    setIsSuccess(false);

    const { username, confirmationCode } = formData;

    if (!username || !confirmationCode) {
      setMessage("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });

    cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
      setIsLoading(false);

      if (err) {
        console.error("Confirmation error:", err);
        let errorMessage = "Confirmation failed. ";
        switch (err.code) {
          case "CodeMismatchException":
            errorMessage += "Invalid confirmation code. Please check your email and try again.";
            break;
          case "ExpiredCodeException":
            errorMessage += "Confirmation code has expired. Please request a new one.";
            break;
          case "NotAuthorizedException":
            errorMessage += "User is already confirmed or does not exist.";
            break;
          default:
            errorMessage += err.message;
        }
        setMessage(errorMessage);
        return;
      }

      console.log("Confirmation successful:", result);
      setIsSuccess(true);
      setMessage("Registration confirmed successfully! You can now log in.");
      localStorage.removeItem("pendingUsername");
      setTimeout(() => navigate("/login"), 2000);
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        px: 2,
        backgroundColor: theme.backgroundColor,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          backgroundColor: theme.backgroundColor,
          backgroundImage: "none",
          borderRadius: 2,
          boxShadow: `0 8px 32px ${theme.primaryColor}20`,
          border: `1px solid ${theme.primaryColor}20`,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, textAlign: "center", color: theme.primaryColor, mb: 3 }}
        >
          Confirm Registration
        </Typography>

        <Box component="form" onSubmit={handleConfirm} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Username"
            name="username"
            id="username"
            value={formData.username}
            onChange={handleChange}
            required
            fullWidth
            size="small"
            InputLabelProps={{ style: { color: theme.textColor } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.textColor,
                "& fieldset": { borderColor: `${theme.primaryColor}40` },
                "&:hover fieldset": { borderColor: theme.primaryColor },
                "&.Mui-focused fieldset": { borderColor: theme.primaryColor },
              },
            }}
          />

          <TextField
            label="Confirmation Code"
            name="confirmationCode"
            id="confirmationCode"
            value={formData.confirmationCode}
            onChange={handleChange}
            required
            fullWidth
            size="small"
            InputLabelProps={{ style: { color: theme.textColor } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.textColor,
                "& fieldset": { borderColor: `${theme.primaryColor}40` },
                "&:hover fieldset": { borderColor: theme.primaryColor },
                "&.Mui-focused fieldset": { borderColor: theme.primaryColor },
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{
              mt: 1,
              backgroundColor: theme.primaryColor,
              color: theme.buttonTextColor || "#fff",
              fontWeight: 600,
              "&:hover": { backgroundColor: theme.accentColor },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            {isLoading ? (
              <CircularProgress size={22} sx={{ color: theme.buttonTextColor || "#fff" }} />
            ) : (
              "Confirm Registration"
            )}
          </Button>
        </Box>

        {message && (
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              textAlign: "center",
              color: isSuccess ? theme.primaryColor : theme.secondaryColor,
              fontWeight: 500,
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ConfirmSignUp;
